import React, { useCallback, useEffect, useState } from 'react';
import { Archive, Bell, BellOff, Check, Clock, Globe, RefreshCw, Shield } from 'lucide-react';
import { subscribeAlertInbox, markAlertAsRead, archiveAlert as archiveAlertRepo } from '../../services/AlertInboxRepository';

const API_BASE = (
  import.meta.env.VITE_RDA_API_BASE_URL ||
  import.meta.env.VITE_BACKEND_BASE_URL ||
  import.meta.env.VITE_BACKEND_URL ||
  'https://rda-backend-thuq.onrender.com'
).replace(/\/+$/, '');

const getAuthHeaders = async () => {
  try {
    const token = await getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
};

const TYPE_META = {
  intimation: {
    label: 'Intimação',
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    Icon: Shield,
  },
  news_alert: {
    label: 'Alerta',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    Icon: Globe,
  },
};

const FILTERS = [
  { key: 'active',      label: 'Caixa de Entrada' },
  { key: 'unread',      label: 'Não Lidos' },
  { key: 'intimation',  label: 'Intimações' },
  { key: 'news_alert',  label: 'Alertas' },
  { key: 'archived',    label: 'Arquivo' },
];

function applyFilter(items, filter) {
  switch (filter) {
    case 'active':     return items.filter((a) => a.status !== 'archived');
    case 'unread':     return items.filter((a) => !a.is_read && a.status !== 'archived');
    case 'intimation': return items.filter((a) => a.type === 'intimation' && a.status !== 'archived');
    case 'news_alert': return items.filter((a) => a.type === 'news_alert' && a.status !== 'archived');
    case 'archived':   return items.filter((a) => a.status === 'archived');
    default:           return items;
  }
}

function formatDate(ts) {
  if (!ts) return '—';
  try {
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

export default function AlertInbox({ syncTrigger = 0, activeFilter = null }) {
  const [alerts, setAlerts]   = useState([]);
  const [filterInternal, setFilterInternal] = useState('active');
  const filter = activeFilter !== null ? activeFilter : filterInternal;
  const setFilter = (v) => setFilterInternal(v);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');

  // Listener em tempo real — alert_inbox, ordenado por data desc
  useEffect(() => {
    const unsub = subscribeAlertInbox((items) => {
      setAlerts(items);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const markAsRead = useCallback(async (id) => {
    await markAlertAsRead(id);
  }, []);

  const archiveAlert = useCallback(async (id) => {
    await archiveAlertRepo(id);
  }, []);

  const triggerSync = useCallback(async () => {
    setSyncing(true);
    setSyncMsg('');
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/alerts/trigger-sync`, {
        method: 'POST',
        headers,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSyncMsg('Sincronização iniciada. Novos alertas aparecerão em segundos.');
    } catch (e) {
      setSyncMsg(`Erro ao sincronizar: ${e.message}`);
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncMsg(''), 6000);
    }
  }, []);

  useEffect(() => {
    if (syncTrigger > 0) triggerSync();
  }, [syncTrigger]);

  const unreadCount = alerts.filter((a) => !a.is_read && a.status !== 'archived').length;
  const visible = applyFilter(alerts, filter);

  return (
    <div className="flex flex-col h-full bg-gray-50">


      {/* ── Listagem ───────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto p-6">

        {loading && (
          <div className="flex items-center justify-center py-24 text-gray-400">
            <RefreshCw size={22} className="animate-spin mr-3" />
            <span className="text-sm">Carregando alertas…</span>
          </div>
        )}

        {!loading && visible.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 gap-4 text-gray-400">
            <BellOff size={44} />
            <p className="text-sm font-medium">Nenhum alerta nesta categoria.</p>
            {filter === 'active' && (
              <p className="text-xs text-gray-300 max-w-xs text-center">
                Clique em "Sincronizar Agora" para buscar novas intimações e notícias.
              </p>
            )}
          </div>
        )}

        {!loading && visible.length > 0 && (
          <div className="space-y-3 max-w-4xl mx-auto">
            {visible.map((alert) => {
              const meta = TYPE_META[alert.type] || TYPE_META.news_alert;
              const { Icon } = meta;
              const isArchived = alert.status === 'archived';
              const isUnread = !alert.is_read && !isArchived;

              return (
                <div
                  key={alert.id}
                  className={`bg-white rounded-xl border shadow-sm p-4 transition-all ${
                    isUnread
                      ? 'border-l-4 border-l-blue-500 border-t-gray-200 border-r-gray-200 border-b-gray-200'
                      : 'border-gray-200'
                  } ${isArchived ? 'opacity-55' : ''}`}
                >
                  {/* Linha superior: tipo + fonte + data */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold ${meta.color}`}>
                        <Icon size={9} />
                        {meta.label}
                      </span>
                      <span className="text-[10px] text-gray-400 font-semibold">
                        {alert.source || '—'}
                      </span>
                      {isUnread && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[9px] font-extrabold tracking-wider">
                          NOVO
                        </span>
                      )}
                      {isArchived && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[9px] font-bold">
                          <Archive size={8} /> Arquivado
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] text-gray-400 whitespace-nowrap flex items-center gap-1 flex-shrink-0">
                      <Clock size={9} />
                      {formatDate(alert.created_at)}
                    </span>
                  </div>

                  {/* Título */}
                  <h3 className={`mt-2 text-sm leading-snug ${isUnread ? 'font-bold text-[#20243B]' : 'font-semibold text-gray-600'}`}>
                    {alert.title || '(Sem título)'}
                  </h3>

                  {/* URL ou preview de conteúdo */}
                  {alert.url && (
                    <a
                      href={alert.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 text-xs text-blue-500 hover:underline truncate block max-w-xl"
                    >
                      {alert.url}
                    </a>
                  )}
                  {alert.content && !alert.url && (
                    <p className="mt-1 text-xs text-gray-500 line-clamp-2">
                      {alert.content}
                    </p>
                  )}

                  {/* Badges dos termos matched (MEGA TAXONOMIA) */}
                  {Array.isArray(alert.matched_terms) && alert.matched_terms.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {alert.matched_terms.map((term, i) => (
                        <span
                          key={i}
                          className="px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[9px] font-bold capitalize"
                        >
                          {term}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Ações de triagem */}
                  {!isArchived && (
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                      {!alert.is_read && (
                        <button
                          type="button"
                          onClick={() => markAsRead(alert.id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold rounded-lg bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition-colors"
                        >
                          <Check size={10} />
                          Marcar como lido
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => archiveAlert(alert.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold rounded-lg bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100 transition-colors"
                      >
                        <Archive size={10} />
                        Arquivar
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
